import React, { useState, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import OfflineMain from "./OfflineOrder/OfflineMain";
import { OfflineLabtestListing } from "./OfflineLabtest/OfflineLabtestListing";

import {
  FileText,
  History,
  CheckCircle,
  Clock,
  Truck,
  MapPin,
  User,
  ShoppingCart,
  Package,
  TestTube,
  ChevronLeft,
  ChevronRight,
  X,
  ZoomIn,
  ArrowLeft,
  Download,
  Eye,
  ChevronLeftCircle,
} from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import { OrderService } from "../../../../services/api/order.service";
import arrow from "../../../../assets/images/arrow.png";
import { InverntroyService } from "../../../../services/api/inventroy.service";
import toast from "react-hot-toast";
import { DataTable } from "../../../DataTable/DataTable";
import { OfflineItemService } from "../../../../services/api/OfflineOrder/offline.service";
import { OfflineOrderDetails } from "./OfflineOrder/OfflineOrderDetails";
import AssignDriver from "./AssignDriver";
import Bill from "../../../../shared/Bill";
interface ProductPrice {
  id: string;
  product_id: string;
  selling_discount: string;
  final_selling_price: string;
  product_mrp: string;
  category_id: number;
}

interface CategoryDetails {
  id: string;
  categoryName: string;
  categoryIcon: string;
}
interface TlabTestDetails {
  labtestName: string;
  labtestCategory: string;
  labtestPrice: string;
  includedTests: string;
  labtestDescription: string;
  labtestReportTime: string;
  labtestSlot: string;
}
interface ProductOrderItem {
  productName: string;
  productDescription: string;
  categoryName: string;
  productImage: string;
  saltNames: string;
  productPrice: ProductPrice;
  productsItemsCount: number;
}

interface PrescriptionDetails {
  id: number;
  source: string;
  prescriptionImage: string;
  note: string;
}

interface ClinicDetails {
  clinicName: string;
  clinicProfileImage: string | null;
  clinicSupervisorName: string;
  clinicAddress: string;
  clinicPincode: string;
  clinicMobileNumber: string;
}

interface PatientDetails {
  patientName: string;
  patientProfileImage: string;
  patientMobileNumber: string;
  patientAddress: string;
  patientPincode: string;
}

interface DeliveryPersonDetails {
  deliveryPersonName: string;
  deliveryPersonProfileImage: string;
  deliveryPersonMobileNumber: string;
  deliveryPersonVehicleNumber: string;
}

interface Order {
  id: number;
  patientId: string;
  orderNumber: string;
  orderType: string;
  orderDate: string;
  orderStatus: string;
  orderTotal: number;
  paymentId: string | null;
  paymentStatus: string | null;
  isPrescriptionRequired: boolean;
  hasPrescription: boolean;
  orderAddress: string;
  orderPincode: string;
  clinicId: string | null;
  deliveryPersonId: string | null;
  productOrderItems: ProductOrderItem[];
  prescriptionDetails: PrescriptionDetails;
  clinicDetails: ClinicDetails | null;
  patientDetails: PatientDetails;
  deliveryPersonDetails: DeliveryPersonDetails;
  labtestDetails?: TlabTestDetails[];
}

interface OrderHistoryProps {
  onBack: () => void;
}
const getStatusColor = (status: string) => {
  switch (status) {
    case "CLINIC_ASSIGNED":
      return "bg-green-100 text-green-700";
    case "PENDING_PATIENT":
      return "bg-yellow-100 text-yellow-700";
    case "PENDING_ADMIN":
      return "bg-orange-100 text-orange-700";
    case "OUT_FOR_DELIVERY":
      return "bg-blue-100 text-blue-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case "CLINIC_ASSIGNED":
      return <Truck size={14} />;
    case "PENDING_PATIENT":
      return <Clock size={14} />;
    case "PENDING_ADMIN":
      return <Clock size={14} />;
    case "OUT_FOR_DELIVERY":
      return <Truck size={14} />;
    default:
      return <Clock size={14} />;
  }
};

// Custom Pagination Component
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}> = ({ currentPage, totalPages, onPageChange, isLoading = false }) => {
  const getVisiblePageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (totalPages <= 1) return null;

  const visiblePages = getVisiblePageNumbers();

  return (
    <div className="flex items-center justify-center gap-2 py-4 px-4 border-t border-gray-200 bg-gray-50">
      {/* Previous Button */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1 || isLoading}
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-500"
      >
        <ChevronLeft size={16} />
        Previous
      </button>

      {/* Page Numbers */}
      <div className="flex items-center gap-1">
        {visiblePages.map((page, index) => {
          if (page === "...") {
            return (
              <span key={index} className="px-3 py-2 text-sm text-gray-500">
                ...
              </span>
            );
          }

          const pageNumber = page as number;
          const isCurrentPage = pageNumber === currentPage;

          return (
            <button
              key={pageNumber}
              onClick={() => onPageChange(pageNumber)}
              disabled={isLoading}
              className={`px-3 py-2 text-sm font-medium rounded-md disabled:cursor-not-allowed ${
                isCurrentPage
                  ? "bg-blue-600 text-white border border-blue-600"
                  : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              {pageNumber}
            </button>
          );
        })}
      </div>

      {/* Next Button */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || isLoading}
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-500"
      >
        Next
        <ChevronRight size={16} />
      </button>
    </div>
  );
};

// Order Listing Sidebar Component
const OrderListingSidebar: React.FC<{
  orders: Order[];
  selectedOrder: Order | null;
  onOrderSelect: (order: Order) => void;
  isLoading: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
  };
  selectedTab: string;
  onPageChange: (page: number) => void;
}> = ({
  orders,
  selectedOrder,
  onOrderSelect,
  isLoading,
  pagination,
  onPageChange,
  selectedTab,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      {selectedTab === "offline" ? null : (
        <>
          <div className="w-1/4 border-r border-gray-200 flex flex-col h-full">
            {/* Orders List */}
            <div className="flex-1 overflow-y-auto hiddenScrollbar">
              {isLoading ? (
                <div className="p-4 text-center text-gray-500">
                  Loading orders...
                </div>
              ) : orders.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No orders found
                </div>
              ) : (
                <div className="space-y-1 bg-gray-50">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      className={`hover:shadow-xl transition-shadow hover:border hover:border-gray-300 p-3 cursor-pointer hover:bg-white ${
                        selectedOrder?.id === order.id
                          ? "bg-white shadow-xl border border-gray-300"
                          : ""
                      }`}
                      onClick={() => onOrderSelect(order)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <CheckCircle size={16} className="text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            {/* <h4 className="font-medium text-sm text-gray-900 truncate capitalize">
                        {order.orderType?.replaceAll("_", " ")}
                      </h4> */}
                          </div>
                          <p className="text-xs text-gray-600 mb-1">
                            Order ID: {order.orderNumber}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">
                              {formatDate(order.orderDate)}
                            </span>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(
                                order.orderStatus
                              )}`}
                            >
                              {getStatusIcon(order.orderStatus)}
                              {order.orderStatus.replace("_", " ")}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={onPageChange}
              isLoading={isLoading}
            />
          </div>
        </>
      )}
    </>
  );
};

// Order Details View Component
const OrderDetailsView: React.FC<{
  selectedOrder: Order | null;
  orderLoading: boolean;
  fetchOrder:any;
}> = ({ selectedOrder, orderLoading,fetchOrder }) => {
  const [selectedPrescription, setSelectedPrescription] = useState<
    PrescriptionDetails[]
  >([]);

  const [SelectedOrderIdForBill, setSelectedOrderIdForBill] = useState("");
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getPaymentStatusColor = (status: string | null) => {
    if (status === "Completed") return "text-green-600 font-semibold";
    if (status === "Pending") return "text-yellow-600 font-semibold";
    return "text-red-600 font-semibold";
  };

  if (orderLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          Loading order details...
        </div>
      </div>
    );
  }

  if (!selectedOrder) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          Select an order to view details
        </div>
      </div>
    );
  }

  if(SelectedOrderIdForBill){
    return <Bill orderId={selectedOrder.id} orderType="ONLINE" onClose={() => setSelectedOrderIdForBill("")}/>
  }

  return (
    <div className="bg-white h-full overflow-y-auto">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="text-gray-700 text-sm">Order ID: </span>
            <span className="text-gray-900 font-medium">
              {selectedOrder.orderNumber}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-700 text-sm">Payment :</span>
            <span
              className={getPaymentStatusColor(selectedOrder.paymentStatus)}
            >
              {selectedOrder.paymentStatus === "Completed"
                ? "Paid"
                : selectedOrder.paymentStatus || "Pending"}
            </span>
          </div>
        </div>

        {/* Order Details Header */}
        <h3 className="text-gray-900 font-medium mb-4">Order Details</h3>

        {/* Main Content Layout */}
        <div className="flex gap-8">
          {/* Left Side - Products */}
          <div className="flex-1">
            <div className="space-y-4 mb-8">
              {selectedOrder.productOrderItems?.map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                    <img
                      src={item.productImage}
                      alt={item.productName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML =
                            '<div class="w-8 h-8 bg-blue-200 rounded"></div>';
                        }
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 text-sm mb-1">
                      {item.productName}
                    </h4>
                    <p className="text-xs text-gray-500">
                      {item.productDescription}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900 bg-gray-100 rounded-full w-8 h-8 flex items-center justify-center">
                      {item.productsItemsCount}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Prescription and Bill */}
          <div className="w-48">
            <div className="space-y-4">
              {selectedOrder.hasPrescription && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900 font-medium text-sm">
                      Prescription
                    </span>
                    <button
                      onClick={() =>
                        setSelectedPrescription(
                          selectedOrder?.prescriptionDetails
                        )
                      }
                      className="text-cyan-500 hover:underline text-sm font-medium"
                    >
                      View
                    </button>
                  </div>
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 font-medium text-sm">
                    Bill
                  </span>
                  <button
                    onClick={() =>
                      setSelectedOrderIdForBill(String(selectedOrder.id))
                    }
                    className="text-cyan-500 hover:underline text-sm font-medium"
                  >
                    View
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Address */}
        <div className="my-4">
          <div className="flex items-start gap-2 mb-2">
            <MapPin size={16} className="text-red-500 mt-1" />
            <span className="text-gray-900 font-medium text-sm">
              Delivery Address
            </span>
          </div>
          <div className="ml-6 text-gray-700 text-sm">
            <p>{selectedOrder.orderAddress}</p>
            <p>Pin Code: {selectedOrder.orderPincode}</p>
          </div>
        </div>

        {/* Bottom Section */}

        <div className="flex items-center justify-between gap-8 w-full my-4">
          {/* Ordered by */}
          <div>
            <h4 className="text-gray-600 text-sm mb-3">Ordered by</h4>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                {selectedOrder.patientDetails?.patientProfileImage ? (
                  <img
                    src={selectedOrder.patientDetails.patientProfileImage}
                    alt={selectedOrder.patientDetails.patientName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-300">
                    <User size={16} className="text-gray-500" />
                  </div>
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">
                  {selectedOrder.patientDetails?.patientName}
                </p>
                <p className="text-xs text-gray-600">
                  +91 {selectedOrder.patientDetails?.patientMobileNumber}
                </p>
                <p className="text-xs text-gray-500">
                  {formatDate(selectedOrder.orderDate)},{" "}
                  {formatTime(selectedOrder.orderDate)}
                </p>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="hidden xl:block">
            <img src={arrow} alt="arrow" className="w-11 h-4" />
          </div>

          {/* Assigned to Delivery boy */}
          {selectedOrder.deliveryPersonDetails ? (
            <div>
              <h4 className="text-gray-600 text-sm mb-3">
                Assigned to Delivery boy
              </h4>
              <div className="flex items-center gap-3 bg-gray-300/20 p-3 rounded">
                <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                  {selectedOrder.deliveryPersonDetails
                    .deliveryPersonProfileImage ? (
                    <img
                      src={
                        selectedOrder.deliveryPersonDetails
                          .deliveryPersonProfileImage
                      }
                      alt={
                        selectedOrder.deliveryPersonDetails.deliveryPersonName
                      }
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-300">
                      <User size={16} className="text-gray-500" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">
                    {selectedOrder.deliveryPersonDetails.deliveryPersonName}
                  </p>
                  <p className="text-xs text-gray-600">
                    Bike No:
                    {
                      selectedOrder.deliveryPersonDetails
                        .deliveryPersonVehicleNumber
                    }
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-gray-300/20 p-3 rounded">
              <AssignDriver orderId={selectedOrder.id} clinicId={selectedOrder.clinicId??""} onSuccess={fetchOrder}/>
            </div>
          )}
        </div>
        {/* Payment and items count */}
        <div className="border my-8 border-[#DBDBDB] rounded-md shadow-sm w-[70%]">
          <div className="flex items-center gap-2 p-2 border-b border-gray-400/30">
            <ShoppingCart size={16} className="text-gray-600" />
            <span className="text-lg text-[#000000]">
              {selectedOrder.productOrderItems?.length || 0} items
            </span>
          </div>
          <div className="flex items-center justify-between p-2">
            <div className="">
              <p className="text-sm text-gray-700 mb-1">Total payable</p>
              <p className="text-xl font-bold text-gray-900">
                {selectedOrder.orderTotal.toFixed(2)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-green-600" />
              <span className="text-sm text-green-600 font-medium">
                {selectedOrder.paymentStatus === "COMPLETED"
                  ? "Patient Approved"
                  : "Payment Pending"}
              </span>
            </div>
          </div>
        </div>
      </div>
      {selectedPrescription.length > 0 && (
        <ViewPrescription
          prescription={selectedPrescription}
          onClose={() => setSelectedPrescription([])}
        />
      )}
      {/* {SelectedOrderIdForBill && (
        <MedicalBill
          orderId={SelectedOrderIdForBill}
          onClose={() => setSelectedOrderIdForBill("")}
        />
      )} */}
      {SelectedOrderIdForBill && (
        <Bill orderId={selectedOrder.id} orderType="ONLINE" onClose={() => setSelectedOrderIdForBill("")}/>
      )}
    </div>
  );
};

// lab test details view
const LabTestOrderDetailsView: React.FC<{
  selectedOrder: Order | null;
  orderLoading: boolean;
  onsuccess: () => void;
}> = ({ selectedOrder, orderLoading, onsuccess }) => {
  const [categories, setCategories] = useState<CategoryDetails[]>([]);
  const [selectedPrescription, setSelectedPrescription] = useState<
    PrescriptionDetails[]
  >([]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatPrice = (price: number) => {
    return `₹ ${price.toLocaleString("en-IN")}`;
  };

  const fetchLabTestCategories = async () => {
    try {
      const response = await InverntroyService.getLabTestCategory();
      console.log("API Response:", response);

      if (Array.isArray(response)) {
        setCategories(response);
        console.log("Categories set:", response);
      } else {
        if (response?.success && Array.isArray(response.data)) {
          setCategories(response.data);
        } else {
          console.warn("Unexpected response format:", response);
          toast.error("Failed to load categories - unexpected format");
        }
      }
    } catch (error) {
      console.error("Error fetching lab test categories:", error);
      toast.error("Failed to load categories");
    }
  };

  const handleMarkAsReceived = async (orderId: string) => {
    try {
      const response =
        await InverntroyService.handleUpdateLabtestStatusByClinic({
          orderId,
          orderStatus: "FORWARDED_TO_LAB",
        });
      if (response.success) {
        toast.success(response.message);
        onsuccess();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.log("\n\n error", error, "\n\n");
      toast.error(`${error}`);
    }
  };
  useEffect(() => {
    fetchLabTestCategories();
  }, []);

  const getPaymentStatusColor = (status: string | null) => {
    if (status === "Completed" || status === "COMPLETED")
      return "text-green-600 font-semibold";
    if (status === "Pending") return "text-orange-600 font-semibold";
    return "text-red-600 font-semibold";
  };

  const getPaymentStatusText = (status: string | null) => {
    if (status === "Completed" || status === "COMPLETED") return "Paid";
    return "Pending";
  };

  if (orderLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          Loading order details...
        </div>
      </div>
    );
  }

  if (!selectedOrder) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          Select an order to view details
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white h-full overflow-y-auto">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="text-gray-700 text-sm">Order ID: </span>
            <span className="text-gray-900 font-medium">
              {selectedOrder.orderNumber}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-700 text-sm">Payment:</span>
            <span
              className={getPaymentStatusColor(selectedOrder.paymentStatus)}
            >
              {getPaymentStatusText(selectedOrder.paymentStatus)}
            </span>
          </div>
        </div>

        {/* Order Details Header */}
        <h3 className="text-gray-900 font-medium mb-4">Order Details</h3>

        {/* Lab Tests List */}
        <div className="space-y-4 mb-8">
          {selectedOrder.labtestDetails?.map((item, index) => (
            <div
              key={index}
              className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg"
            >
              {/* Test Icon */}
              <div className="w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center">
                {(() => {
                  const category = categories.find(
                    (cat) => cat.categoryName === item.labtestCategory
                  );
                  return category?.categoryIcon ? (
                    <img
                      src={category.categoryIcon}
                      alt={category.categoryName}
                      className="w-8 h-8 object-contain"
                    />
                  ) : (
                    <div className="w-6 h-6 bg-orange-400 rounded"></div>
                  );
                })()}
              </div>

              {/* Test Details */}
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 text-base mb-1">
                  {item.labtestName}
                </h4>
                <p className="text-sm text-gray-600 mb-2">
                  {item.labtestDescription}
                </p>
                {item.includedTests && (
                  <p className="text-xs text-gray-500">
                    Includes: {item.includedTests}
                  </p>
                )}
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span>Category: {item.labtestCategory}</span>
                  <span>Report Time: {item.labtestReportTime}</span>
                  <span>Slot: {item.labtestSlot}</span>
                </div>
              </div>

              {/* Price and Count */}
              <div className="text-right">
                <div className="text-lg font-semibold text-green-600 mb-2">
                  {formatPrice(parseInt(item.labtestPrice))}
                </div>
                <div className="text-sm text-gray-500">1 Test</div>
              </div>
            </div>
          ))}
        </div>

        {/* Patient Details Section */}
        <div className="mb-6">
          <h4 className="text-gray-900 font-medium text-sm mb-3">
            Patient Details
          </h4>
          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
            <div className="w-12 h-12 bg-blue-200 rounded-full overflow-hidden flex-shrink-0">
              {selectedOrder.patientDetails?.patientProfileImage ? (
                <img
                  src={selectedOrder.patientDetails.patientProfileImage}
                  alt={selectedOrder.patientDetails.patientName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-blue-300">
                  <User size={20} className="text-blue-600" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900 text-sm">
                {selectedOrder.patientDetails?.patientName}
              </p>
              <p className="text-xs text-gray-600">
                +91 {selectedOrder.patientDetails?.patientMobileNumber}
              </p>
              <p className="text-xs text-gray-500">
                Age:{" "}
                {selectedOrder.labtestDetails?.[0]?.patientDetails?.patientAge}
              </p>
            </div>
          </div>
        </div>

        {/* Delivery Address */}
        <div className="mb-6">
          <div className="flex items-start gap-2 mb-2">
            <MapPin size={16} className="text-red-500 mt-1" />
            <span className="text-gray-900 font-medium text-sm">
              Patient Address
            </span>
          </div>
          <div className="ml-6 text-gray-700 text-sm bg-gray-50 p-3 rounded-lg">
            <p>{selectedOrder.orderAddress}</p>
            <p>Pin Code: {selectedOrder.orderPincode}</p>
          </div>
        </div>

        {/* Order Timeline */}
        <div className="flex items-center justify-between gap-8 w-full mb-6">
          {/* Ordered by */}
          <div className="flex-1">
            <h4 className="text-gray-600 text-sm mb-3">Ordered by</h4>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                {selectedOrder.patientDetails?.patientProfileImage ? (
                  <img
                    src={selectedOrder.patientDetails.patientProfileImage}
                    alt={selectedOrder.patientDetails.patientName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-300">
                    <User size={16} className="text-gray-500" />
                  </div>
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">
                  {selectedOrder.patientDetails?.patientName}
                </p>
                <p className="text-xs text-gray-600">
                  +91 {selectedOrder.patientDetails?.patientMobileNumber}
                </p>
                <p className="text-xs text-gray-500">
                  {formatDate(selectedOrder.orderDate)},{" "}
                  {formatTime(selectedOrder.orderDate)}
                </p>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="hidden xl:block">
            <img src={arrow} alt="arrow" className="w-11 h-4" />
          </div>
          {/* Assigned to Delivery person */}
          <div className="flex-1">
            {selectedOrder.deliveryPersonDetails ? (
              <div>
                <h4 className="text-gray-600 text-sm mb-3">
                  Assigned to Sample Pickup Person
                </h4>
                <div className="flex items-center gap-3 bg-green-50 p-3 rounded-lg">
                  <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                    {selectedOrder.deliveryPersonDetails
                      .deliveryPersonProfileImage ? (
                      <img
                        src={
                          selectedOrder.deliveryPersonDetails
                            .deliveryPersonProfileImage
                        }
                        alt={
                          selectedOrder.deliveryPersonDetails.deliveryPersonName
                        }
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-300">
                        <User size={16} className="text-gray-500" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      {selectedOrder.deliveryPersonDetails.deliveryPersonName}
                    </p>
                    <p className="text-xs text-gray-600">
                      +91{" "}
                      {
                        selectedOrder.deliveryPersonDetails
                          .deliveryPersonMobileNumber
                      }
                    </p>
                    <p className="text-xs text-gray-500">
                      Vehicle:{" "}
                      {
                        selectedOrder.deliveryPersonDetails
                          .deliveryPersonVehicleNumber
                      }
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <h4 className="text-gray-600 text-sm mb-3">
                  Sample Pickup Person
                </h4>
                <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                  <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                    <div className="w-full h-full flex items-center justify-center bg-gray-300">
                      <User size={16} className="text-gray-500" />
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-sm text-gray-900">
                      Not assigned yet
                    </div>
                    <div className="text-xs text-gray-500">
                      Pickup person will be assigned soon
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment Summary */}
        <div className="border border-gray-200 rounded-lg shadow-sm max-w-2xl">
          <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-gray-50">
            <ShoppingCart size={18} className="text-gray-600" />
            <span className="text-lg font-medium text-gray-900">
              {selectedOrder.labtestDetails?.length || 0}{" "}
              {selectedOrder.labtestDetails?.length === 1 ? "Test" : "Tests"}
            </span>
          </div>

          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total payable</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatPrice(selectedOrder.orderTotal)}
                </p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="text-sm text-green-600 font-medium">
                    {selectedOrder.paymentStatus === "COMPLETED" ||
                    selectedOrder.paymentStatus === "Completed"
                      ? "Patient Approved"
                      : "Payment Pending"}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Payment Method:{" "}
                  {selectedOrder.paymentDetails?.paymentMethod || "COD"}
                </p>
              </div>
            </div>

            {/* Mark as Received Button - similar to the image */}
            {selectedOrder.orderStatus === "FORWARDED_TO_LAB" && (
              <p className="text-xs font-medium bg-gray-300/40 w-fit p-2 rounded-lg">
                {selectedOrder.orderStatus}
              </p>
            )}
            {selectedOrder.orderStatus === "CLINIC_RECIEVED" && (
              <button
                onClick={() => handleMarkAsReceived(String(selectedOrder.id))}
                className="w-[320px] bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
              >
                Mark as Sample Collected
              </button>
            )}
          </div>
        </div>
      </div>

      {selectedPrescription.length > 0 && (
        <ViewPrescription
          prescription={selectedPrescription}
          onClose={() => setSelectedPrescription([])}
        />
      )}
    </div>
  );
};

const OutOfDeliveryData: React.FC<{
  orders: Order[];
  isLoading: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
  };
  onPageChange: (page: number) => void;
}> = ({ orders, isLoading, pagination, onPageChange }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getPaymentMethodText = (paymentStatus: string | null) => {
    if (paymentStatus === "COMPLETED") return "Online";
    return "COD";
  };

  const getOrderTypeIcon = (orderType: string, hasPrescription: boolean) => {
    if (hasPrescription || orderType === "PRESCRIPTION") {
      return (
        <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
          <TestTube className="w-4 h-4 text-green-600" />
        </div>
      );
    }
    return (
      <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
        <Package className="w-4 h-4 text-green-600" />
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full">
        <div className="text-center text-gray-500">Loading orders...</div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex items-center justify-center w-full">
        <div className="text-center text-gray-500">No orders found</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 w-full flex flex-col h-full">
      {/* Table */}
      <div className="flex-1 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">
                Order & Order ID
              </th>
              <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">
                Patient Name & Number
              </th>
              <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">
                Dispatch Date
              </th>
              <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">
                Payment Status
              </th>
              <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">
                Payment Method
              </th>
              <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">
                Driver Name & Number
              </th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr
                key={order.id}
                className="border-b border-gray-100 hover:bg-gray-50"
              >
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    {getOrderTypeIcon(order.orderType, order.hasPrescription)}
                    <div>
                      <div className="font-medium text-sm text-gray-900 capitalize">
                        {order.hasPrescription ? "Medicines" : "Product"}
                      </div>
                      <div className="text-xs text-gray-500">
                        Order ID: {order.orderNumber}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                      {order.patientDetails?.patientProfileImage ? (
                        <img
                          src={order.patientDetails.patientProfileImage}
                          alt={order.patientDetails.patientName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-300">
                          <User size={14} className="text-gray-500" />
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-sm text-gray-900">
                        {order.patientDetails?.patientName || "Unknown"}
                      </div>
                      <div className="text-xs text-gray-500">
                        +91 {order.patientDetails?.patientMobileNumber || "N/A"}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="text-sm text-gray-900">
                    {formatDate(order.orderDate)}
                  </div>
                </td>
                <td className="py-4 px-4">{order.paymentStatus}</td>
                <td className="py-4 px-4">
                  <div className="text-sm text-gray-900">
                    {getPaymentMethodText(order.paymentStatus)}
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                      {order.deliveryPersonDetails
                        ?.deliveryPersonProfileImage ? (
                        <img
                          src={
                            order.deliveryPersonDetails
                              .deliveryPersonProfileImage
                          }
                          alt={order.deliveryPersonDetails.deliveryPersonName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-300">
                          <User size={14} className="text-gray-500" />
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-sm text-gray-900">
                        {order.deliveryPersonDetails?.deliveryPersonName ||
                          "Not Assigned"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {order.deliveryPersonDetails?.deliveryPersonMobileNumber
                          ? `+91 ${order.deliveryPersonDetails.deliveryPersonMobileNumber}`
                          : "N/A"}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        onPageChange={onPageChange}
        isLoading={isLoading}
      />
    </div>
  );
};

interface OfflineOrderTableProps {
  setOfflineCount: (count: number) => void;
  onViewDetails: (show: boolean ) => void;
}

const OfflineOrderTable = ({ setOfflineCount, onViewDetails }: OfflineOrderTableProps) => {
  const [offlineOrders, setOfflineOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const { user } = useAuth();
  const clinicId = user.role === "clinic" ? user.id : "";
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItemsCount: 0,
    totalPages: 1,
    nextPage: null as number | null,
    previousPage: null as number | null,
  });

  useEffect(() => {
    const fetchOfflineOrders = async () => {
      try {
        setIsLoading(true);
        const response = await OfflineItemService.getOfflineOrderList({
          page: Number(pagination.currentPage),
          pageSize: Number(pagination.itemsPerPage),
          clinicId: clinicId,
        });

        if (response && response.success) {
          setOfflineOrders(response.data.data);
          setPagination((prev) => ({
            ...prev,
            totalItemsCount: response.data.pagination.totalCount,
            totalPages: response.data.pagination.totalPages,
            nextPage: response.data.pagination.hasNextPage
              ? prev.currentPage + 1
              : null,
            previousPage: response.data.pagination.hasPrevPage
              ? prev.currentPage - 1
              : null,
          }));
          setOfflineCount(response.data.pagination.totalCount);
        }
      } catch (error) {
        console.error("Failed to fetch orders:", error);
        // Optionally show error to user
      } finally {
        setIsLoading(false);
      }
    };
    fetchOfflineOrders();
  }, [pagination.currentPage, pagination.itemsPerPage, setOfflineCount]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setPagination((prev) => ({ ...prev, itemsPerPage, currentPage: 1 }));
  };

  const fetchOrderDetails = async (orderId: string) => {
    try {
      setIsLoading(true);
      const response = await OfflineItemService.getOfflineOrderDetailsByOrderId(
        orderId
      );
      if (response && response.success) {
        onViewDetails(response.data);
      
      }
    } catch (error) {
      console.error("Failed to fetch order details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (orderId: string) => {
    setSelectedOrderId(orderId);
    fetchOrderDetails(orderId);
  };

  const handleBackToList = () => {
    setShowDetails(false);
    setOrderDetails(null);
  };

  const columns = [
    {
      key: "orderId",
      title: "Order ID",
      sortable: true,
      render: (offline: any) => <div>{offline.invoiceNumber || "N/A"}</div>,
    },
    {
      key: "patientName",
      title: "Patient Name",
      sortable: true,
      render: (offline: any) => <div>{offline.patientName || "N/A"}</div>,
    },
    {
      key: "patientMobileNumber",
      title: "Mobile Number",
      sortable: true,
      render: (offline: any) => (
        <div>{offline.patientMobileNumber || "N/A"}</div>
      ),
    },
    {
      key: "doctorName",
      title: "Doctor Name",
      sortable: true,
      render: (offline: any) => <div>{offline.doctorName || "N/A"}</div>,
    },
    {
      key: "totalItems",
      title: "Items",
      sortable: true,
      render: (offline: any) => <div>{offline.totalItems || 0}</div>,
    },
    {
      key: "totalAmount",
      title: "Amount",
      sortable: true,
      render: (offline: any) => <div>₹{offline.totalAmount || "0.00"}</div>,
    },
    {
      key: "action",
      title: "Action",
      render: (offline: any) => (
        <button
          onClick={() => handleViewDetails(offline.orderId)}
          className="text-blue-500 hover:text-blue-700"
        >
          <Eye className="w-5 h-5" />
        </button>
      ),
    },
  ];

  if (showDetails && orderDetails) {
    return (
      <div className="p-4">
        {/* <button
          onClick={handleBackToList}
          className="flex items-center gap-2 mb-4 text-blac"
        >
          <ChevronLeftCircle size={20} />
          Back to Orders
        </button> */}
        <OfflineOrderDetails data={orderDetails} setShowOfflineOrderDetails={() => {}} />
      </div>
    );
  }

  return (
    <div className="p-4">
      <DataTable
        data={offlineOrders}
        columns={columns}
        itemsPerPage={pagination.itemsPerPage}
        isLoading={isLoading}
        pagination={{
          currentPage: pagination.currentPage,
          setCurrentPage: handlePageChange,
          setPageSize: handleItemsPerPageChange,
          totalPages: pagination.totalPages,
          totalItems: pagination.totalItemsCount,
          onPageChange: handlePageChange,
          onItemsPerPageChange: handleItemsPerPageChange,
        }}
      />
    </div>
  );
};

export const ClinicStockOrdersView: React.FC = () => {
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState<"medical" | "lab" | "offline" | "offline-labtest">(
    "medical"
  );
  const [orderStatus, setOrderStatus] = useState<string>("CLINIC_ASSIGNED");
  const [orderType, setOrderType] = useState<string>("DIRECT");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [orderLoading, setOrderLoading] = useState<boolean>(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [showOrderHistory, setShowOrderHistory] = useState<boolean>(false);
  const [showOfflineOrder, setShowOfflineOrder] = useState(false);
  const [showOfflineOrderDetails, setShowOfflineOrderDetails] = useState(false);
  const [selectedOfflineOrder, setSelectedOfflineOrder] = useState(null);

  const clinicId = user?.role === "clinic" ? user?.id : "";
  const [orderCount, setOrderCount] = useState(0);
  const [labTestcount, setlabTestcount] = useState(0);
  const [offlineCount, setOfflineCount] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItemsCount: 0,
    totalPages: 1,
    nextPage: null as number | null,
    previousPage: null as number | null,
  });

  const fetchLabTestOrder = async (page: number = pagination.currentPage) => {
    try {
      console.log("\n\n FETCHING THE LAB TEST DETAILS");
      setIsLoading(true);
      const filter: any = { clinicId: clinicId, isLabtestOrder: true };
      const response = await OrderService.getOrdersListing({
        page,
        pageSize: pagination.itemsPerPage,
        filter,
      });

      if (response?.data?.length > 0) {
        const orders = response?.data;
        setOrders(orders);
        setlabTestcount(orders?.length);
        if (orders?.length > 0) {
          setSelectedOrderId(orders[0]?.id);
          setSelectedOrder(orders[0]);
        }
        setPagination({
          currentPage: response.pagination.page,
          itemsPerPage: response.pagination.pageSize,
          totalItemsCount: response.pagination.totalCount,
          totalPages: response.pagination.totalPages,
          nextPage: response.pagination.hasNextPage
            ? response.pagination.page + 1
            : null,
          previousPage: response.pagination.hasPrevPage
            ? response.pagination.page - 1
            : null,
        });
      } else {
        setOrders([]);
        setOrderCount(0);
        setSelectedOrder(null);
        setSelectedOrderId(null);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrders = async (page: number = pagination.currentPage) => {
    try {
      setIsLoading(true);
      console.log("\n\n FETCHING THE ORDERS DETAILS");
      const filter: any = { clinicId };

      if (orderStatus !== "OUT_FOR_DELIVERY") {
        if (orderType === "hasPrescription") {
          filter.hasPrescription = true;
        } else {
          filter.orderType = orderType;
        }

        if (orderStatus) {
          filter.orderStatus = orderStatus;
        }
      } else {
        filter.orderStatus = "OUT_FOR_DELIVERY";
      }

      const response = await OrderService.getOrdersListing({
        page,
        pageSize: pagination.itemsPerPage,
        filter,
      });

      if (response?.data?.length > 0) {
        const orders = response?.data;
        setOrders(orders);
        setOrderCount(orders?.length);
        if (orders?.length > 0) {
          setSelectedOrderId(orders[0]?.id);
          setSelectedOrder(orders[0]);
        }
        setPagination({
          currentPage: response.pagination.page,
          itemsPerPage: response.pagination.pageSize,
          totalItemsCount: response.pagination.totalCount,
          totalPages: response.pagination.totalPages,
          nextPage: response.pagination.hasNextPage
            ? response.pagination.page + 1
            : null,
          previousPage: response.pagination.hasPrevPage
            ? response.pagination.page - 1
            : null,
        });
      } else {
        setOrders([]);
        setOrderCount(0);
        setSelectedOrder(null);
        setSelectedOrderId(null);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch order details
  const fetchOrderDetails = async (orderId: number) => {
    setOrderLoading(true);
    try {
      const response = await OrderService.getOrderDetailsById(orderId);
      if (response?.id) {
        setSelectedOrder(response);
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setOrderLoading(false);
    }
  };

  useEffect(() => {
    if (selectedOrderId) {
      fetchOrderDetails(selectedOrderId);
    }
  }, [selectedOrderId]);

  useEffect(() => {
    if (selectedTab === "lab") {
      fetchLabTestOrder();
    } else {
      fetchOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, orderStatus]);

  const handleOrderSelect = (order: Order) => {
    setSelectedOrder(order);
    setSelectedOrderId(order.id);
  };

  const handleOrderTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setOrderType(e.target.value);
  };

  const handleOrderStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setOrderStatus(e.target.value);
  };
  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
    fetchOrders(page);
  };

  const handleOrderHistoryClick = () => {
    setShowOrderHistory(true);
  };

  const handleBackFromHistory = () => {
    setShowOrderHistory(false);
  };

  const onsuccess = () => {
    if (selectedTab === "lab") {
      fetchLabTestOrder();
    } else {
      fetchOrders();
    }
  };

  const handleCreateNewOrder = () => {
    setShowOfflineOrder(true);
  };

  // Show Order History component when showOrderHistory is true
  if (showOrderHistory) {
    return <OrderHistory onBack={handleBackFromHistory} />;
  }
  if (showOfflineOrder) {
    return (
      <div className="w-full h-[600px]">
        
        <OfflineMain
        handleBack={() => setShowOfflineOrder(false)}
          onOrderSuccess={() => {
            setShowOfflineOrder(false);
            setSelectedTab("offline");
          }}
        />
      </div>
    );
  }

  if (showOfflineOrderDetails) {
    return (
      <div className="p-4">
        <OfflineOrderDetails data={selectedOfflineOrder}  setShowOfflineOrderDetails={setShowOfflineOrderDetails}/>
      </div>
    );
  }

  return (
    <>
      <section className="w-full h-[600px] overflow-y-hidden">
        <header className="w-full pr-4 flex items-center justify-between rounded-t-md border-b border-gray-400/30">
          <div className="h-[60px] flex items-center gap-6">
            <button
              className={`px-3 h-full border-b-2 flex items-center gap-2 ${
                selectedTab === "medical"
                  ? "text-green-600 font-semibold border-green-700"
                  : "text-gray-500 border-gray-400/5"
              }`}
              onClick={() => setSelectedTab("medical")}
            >
              <FileText size={18} />
              Online Orders - {orderCount}
            </button>
            <button
              className={`flex h-full border-b-2 items-center gap-2 px-3 ${
                selectedTab === "lab"
                  ? "text-green-600 font-semibold border-green-700"
                  : "text-gray-500 border-gray-400/5"
              }`}
              onClick={() => setSelectedTab("lab")}
            >
              <FileText size={18} className="text-black" />
              Lab test order - {labTestcount}
            </button>
            <button
              className={`flex h-full border-b-2 items-center gap-2 px-3 ${
                selectedTab === "offline"
                  ? "text-green-600 font-semibold border-green-700"
                  : "text-gray-500 border-gray-400/5"
              }`}
              onClick={() => setSelectedTab("offline")}
            >
              <FileText size={18} className="text-black" />
              Offline Orders - {offlineCount}
            </button>
            <button
              className={`flex h-full border-b-2 items-center gap-2 px-3 ${
                selectedTab === "offline-labtest"
                  ? "text-green-600 font-semibold border-green-700"
                  : "text-gray-500 border-gray-400/5"
              }`}
              onClick={() => setSelectedTab("offline-labtest")}
            >
              <TestTube size={18} className="text-black" />
              Offline Labtest
            </button>
          </div>

          {selectedTab !== "lab" && selectedTab !== "offline" && selectedTab !== "offline-labtest" && (
            <>
              <div className="flex items-center gap-4">
                <select
                  className="border rounded px-3 py-1"
                  value={orderType}
                  onChange={handleOrderTypeChange}
                >
                  <option value="DIRECT">Direct Order</option>
                  <option value="hasPrescription">Assigned Order</option>
                </select>
                <select
                  className="border rounded px-3 py-1"
                  value={orderStatus}
                  onChange={handleOrderStatusChange}
                >
                  <option value="CLINIC_ASSIGNED">New Order</option>
                  <option value="DELIVERY_ASSIGNED">Delivery Assigned</option>
                  <option value="OUT_FOR_DELIVERY">Out for delivery</option>
                  <option value="return">Return</option>
                </select>
                <button
                  onClick={handleOrderHistoryClick}
                  className="flex items-center gap-1 text-blue-400 hover:underline"
                >
                  <History size={18} />
                  Order History
                </button>
              </div>
            </>
          )}

          {selectedTab === "offline" && (
            <div className="flex items-center gap-4">
              <button
                className={
                  "flex h-full bg-green-600 items-center px-3 py-2 rounded-lg text-white"
                }
                onClick={handleCreateNewOrder}
              >
                create new order
              </button>
            </div>
          )}
        </header>

        <div className="w-full flex h-[540px] relative">
          {selectedTab === "offline-labtest" ? (
            <div className="w-full h-full p-2">
              <OfflineLabtestListing clinicId={clinicId} />
            </div>
          ) : orderStatus === "OUT_FOR_DELIVERY" ? (
            <OutOfDeliveryData
              orders={orders}
              isLoading={isLoading}
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          ) : (
            <>
              {/* Order Listing Sidebar */}
              <OrderListingSidebar
                orders={orders}
                selectedOrder={selectedOrder}
                onOrderSelect={handleOrderSelect}
                isLoading={isLoading}
                pagination={pagination}
                onPageChange={handlePageChange}
                selectedTab={selectedTab}
              />

              {/* Order Details Section */}
              <div className="flex-1 overflow-y-auto hiddenScrollbar h-full">
                {selectedTab === "lab" ? (
                  <LabTestOrderDetailsView
                    selectedOrder={selectedOrder}
                    orderLoading={orderLoading}
                    onsuccess={onsuccess}
                  />
                ) : selectedTab === "offline" ? (
                  <OfflineOrderTable
                    setOfflineCount={setOfflineCount}
                    onViewDetails={(order) => {
                      setSelectedOfflineOrder(order);
                      setShowOfflineOrderDetails(true);
                    }}
                  />
                ) : (
                  <OrderDetailsView
                    selectedOrder={selectedOrder}
                    orderLoading={orderLoading}
                    fetchOrder={fetchOrders}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
};

const ViewPrescription = ({
  prescription,
  onClose,
}: {
  prescription: PrescriptionDetails[];
  onClose: () => void;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextSlide = () => {
    if (isTransitioning || prescription.length <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % prescription.length);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const prevSlide = () => {
    if (isTransitioning || prescription.length <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex(
      (prev) => (prev - 1 + prescription.length) % prescription.length
    );
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const openPreview = (index: number) => {
    setPreviewIndex(index);
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
  };

  const nextPreview = () => {
    setPreviewIndex((prev) => (prev + 1) % prescription.length);
  };

  const prevPreview = () => {
    setPreviewIndex(
      (prev) => (prev - 1 + prescription.length) % prescription.length
    );
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showPreview) {
        switch (e.key) {
          case "ArrowLeft":
            e.preventDefault();
            prevPreview();
            break;
          case "ArrowRight":
            e.preventDefault();
            nextPreview();
            break;
          case "Escape":
            e.preventDefault();
            closePreview();
            break;
        }
      } else {
        switch (e.key) {
          case "ArrowLeft":
            e.preventDefault();
            prevSlide();
            break;
          case "ArrowRight":
            e.preventDefault();
            nextSlide();
            break;
          case "Escape":
            e.preventDefault();
            onClose();
            break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showPreview, previewIndex, currentIndex, onClose]);

  if (prescription.length === 0) {
    return (
      <div className="bg-gray-900 fixed h-screen w-full top-0 left-0 flex items-center justify-center z-50">
        <div className="text-center">
          <p className="text-white text-xl mb-4">No prescriptions available</p>
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Main Container */}
      <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800 z-50 flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 bg-black/30 backdrop-blur-sm">
          <h2 className="text-white text-xl font-semibold">
            Prescription {currentIndex + 1} of {prescription.length}
          </h2>
          <button
            onClick={onClose}
            className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full transition-all duration-200 hover:scale-110"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Card Carousel */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="relative">
            {/* Card Container */}
            <div className="relative w-80 h-80">
              {prescription.map((data, index) => (
                <div
                  key={data.id}
                  className={`absolute inset-0 transition-all duration-500 ease-in-out cursor-pointer ${
                    index === currentIndex
                      ? "opacity-100 transform translate-x-0 scale-100 z-10"
                      : index < currentIndex
                      ? "opacity-30 transform -translate-x-20 scale-90 z-0"
                      : "opacity-30 transform translate-x-20 scale-90 z-0"
                  }`}
                  onClick={() => openPreview(index)}
                >
                  <div className="bg-white rounded-xl shadow-2xl overflow-hidden h-full group hover:shadow-3xl transition-shadow duration-300">
                    {/* Image */}
                    <div className="h-64 bg-gray-100 flex items-center justify-center overflow-hidden">
                      <img
                        src={data.prescriptionImage}
                        alt={data.source}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.currentTarget.src =
                            "https://via.placeholder.com/300x240/374151/9CA3AF?text=Prescription+Image";
                        }}
                      />
                    </div>

                    {/* Card Info */}
                    <div className="p-4 h-16 flex flex-col justify-center bg-white">
                      <p className="text-gray-800 font-medium text-sm text-center truncate">
                        {data.source}
                      </p>
                      {data.note && (
                        <p className="text-gray-600 text-xs text-center truncate mt-1">
                          {data.note}
                        </p>
                      )}
                    </div>

                    {/* Zoom indicator */}
                    <div className="absolute top-2 right-2 bg-black/50 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ZoomIn className="text-white w-4 h-4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            {prescription.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  disabled={isTransitioning}
                  className="absolute left-[-60px] top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextSlide}
                  disabled={isTransitioning}
                  className="absolute right-[-60px] top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Indicators */}
        {prescription.length > 1 && (
          <div className="flex justify-center items-center space-x-3 p-6 bg-black/20 backdrop-blur-sm">
            {prescription.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (!isTransitioning) {
                    setIsTransitioning(true);
                    setCurrentIndex(index);
                    setTimeout(() => setIsTransitioning(false), 300);
                  }
                }}
                disabled={isTransitioning}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? "bg-white scale-125 shadow-lg"
                    : "bg-white/40 hover:bg-white/70 hover:scale-110"
                }`}
              />
            ))}
          </div>
        )}

        {/* Instructions */}
        <div className="text-center p-4 bg-black/20 backdrop-blur-sm">
          <p className="text-white/60 text-sm">
            Click on card to view full image • Use arrow keys to navigate •
            Press ESC to close
          </p>
        </div>
      </div>

      {/* Scrollable Preview Modal */}
      {showPreview && (
        <div
          className="fixed inset-0 bg-black/95 z-[60] backdrop-blur-sm"
          onClick={closePreview}
        >
          <div className="h-full overflow-y-auto">
            <div className="min-h-full flex items-center justify-center p-4">
              <div
                className="relative max-w-4xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button */}
                <button
                  onClick={closePreview}
                  className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all duration-200 hover:scale-110 backdrop-blur-sm z-10"
                >
                  <X className="w-6 h-6" />
                </button>

                {/* Image */}
                <img
                  src={prescription[previewIndex].prescriptionImage}
                  alt={prescription[previewIndex].source}
                  className="w-full h-auto rounded-lg shadow-2xl"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/800x600/374151/9CA3AF?text=Prescription+Image";
                  }}
                />

                {/* Navigation buttons */}
                {prescription.length > 1 && (
                  <>
                    <button
                      onClick={prevPreview}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextPreview}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}

                {/* Image info */}
                <div className="mt-4 bg-black/80 p-6 rounded-lg backdrop-blur-sm">
                  <h3 className="text-white font-semibold text-lg text-center mb-2">
                    {prescription[previewIndex].source}
                  </h3>
                  {prescription[previewIndex].note && (
                    <p className="text-white/80 text-center mb-3">
                      {prescription[previewIndex].note}
                    </p>
                  )}
                  <p className="text-white/60 text-sm text-center">
                    Image {previewIndex + 1} of {prescription.length}
                  </p>
                  <p className="text-white/40 text-xs text-center mt-2">
                    Press ESC to close • Click outside image to close • Scroll
                    to see full image
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const OrderHistory: React.FC<OrderHistoryProps> = ({ onBack }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItemsCount: 0,
    totalPages: 1,
    nextPage: null as number | null,
    previousPage: null as number | null,
  });

  const clinicId = user?.role === "clinic" ? user?.id : "";

  const fetchOrderHistory = async (page: number = pagination.currentPage) => {
    try {
      setIsLoading(true);
      const filter = {
        clinicId,
        orderStatus: "COMPLETED",
      };

      const response = await OrderService.getOrdersListing({
        page,
        pageSize: pagination.itemsPerPage,
        filter,
      });

      if (response?.data?.length > 0) {
        const orders = response?.data;
        setOrders(orders);
        setPagination({
          currentPage: response.pagination.page,
          itemsPerPage: response.pagination.pageSize,
          totalItemsCount: response.pagination.totalCount,
          totalPages: response.pagination.totalPages,
          nextPage: response.pagination.hasNextPage
            ? response.pagination.page + 1
            : null,
          previousPage: response.pagination.hasPrevPage
            ? response.pagination.page - 1
            : null,
        });
      } else {
        setOrders([]);
        setPagination((prev) => ({
          ...prev,
          totalItemsCount: 0,
          totalPages: 1,
        }));
      }
    } catch (error) {
      console.error("Error fetching order history:", error);
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
    fetchOrderHistory(page);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getPaymentMethodText = (paymentStatus: string | null) => {
    if (paymentStatus === "COMPLETED") return "Online";
    return "COD";
  };

  const getOrderTypeIcon = (orderType: string, hasPrescription: boolean) => {
    if (hasPrescription || orderType === "PRESCRIPTION") {
      return (
        <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
          <TestTube className="w-4 h-4 text-green-600" />
        </div>
      );
    }
    return (
      <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
        <Package className="w-4 h-4 text-blue-600" />
      </div>
    );
  };

  const getStatusColor = (status: string) => {
    if (status === "COMPLETED") return "text-green-600";
    if (status === "RETURNED") return "text-orange-600";
    return "text-gray-600";
  };

  const getStatusText = (status: string) => {
    if (status === "COMPLETED") return "Delivered";
    if (status === "RETURNED") return "Returned";
    return status;
  };

  return (
    <section className="w-full h-[600px] overflow-hidden">
      {/* Header */}
      <header className="w-full pr-4 flex items-center justify-between rounded-t-md border-b border-gray-400/30 bg-white">
        <div className="h-[60px] flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <h2 className="text-lg font-semibold text-gray-900">Order History</h2>
        </div>
        <div className="text-sm text-gray-500">
          Total Orders: {pagination.totalItemsCount}
        </div>
      </header>

      {/* Content */}
      <div className="w-full flex h-[650px] z-12 relative">
        {isLoading ? (
          <div className="flex items-center justify-center w-full">
            <div className="text-center text-gray-500">
              Loading order history...
            </div>
          </div>
        ) : orders.length === 0 ? (
          <div className="flex items-center justify-center w-full">
            <div className="text-center text-gray-500">
              No order history found
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 w-full flex flex-col h-full">
            {/* Table */}
            <div className="flex-1 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">
                      Order & Order ID
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">
                      Patient Name & Number
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">
                      Delivery Date
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">
                      Payment Status
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">
                      Payment Method
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700 text-sm">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr
                      key={order.id}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          {getOrderTypeIcon(
                            order.orderType,
                            order.hasPrescription
                          )}
                          <div>
                            <div className="font-medium text-sm text-gray-900 capitalize">
                              {order.hasPrescription ? "Medicines" : "Products"}
                            </div>
                            <div className="text-xs text-gray-500">
                              Order ID: {order.orderNumber}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                            {order.patientDetails?.patientProfileImage ? (
                              <img
                                src={order.patientDetails.patientProfileImage}
                                alt={order.patientDetails.patientName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gray-300">
                                <User size={14} className="text-gray-500" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-sm text-gray-900">
                              {order.patientDetails?.patientName || "Unknown"}
                            </div>
                            <div className="text-xs text-gray-500">
                              +91{" "}
                              {order.patientDetails?.patientMobileNumber ||
                                "N/A"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm text-gray-900">
                          {formatDate(order.orderDate)}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`text-sm font-medium ${
                            order.paymentStatus === "COMPLETED"
                              ? "text-green-600"
                              : "text-yellow-600"
                          }`}
                        >
                          {order.paymentStatus}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm text-gray-900">
                          {getPaymentMethodText(order.paymentStatus)}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`text-sm font-medium ${getStatusColor(
                            order.orderStatus
                          )}`}
                        >
                          {getStatusText(order.orderStatus)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
    </section>
  );
};

const MedicalBill = ({
  orderId,
  onClose,
}: {
  orderId: string;
  onClose: () => void;
}) => {
  const componentRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [billData, setBillData] = useState(null);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const calculateTotalMRP = () => {
    return billData?.productDetails.reduce((sum, item) => {
      return sum + parseFloat(item.productMRP) * item.quantity;
    }, 0);
  };

  const calculateSubtotal = () => {
    return billData?.productDetails.reduce((sum, item) => {
      return sum + parseFloat(item.finalSellingPrice) * item.quantity;
    }, 0);
  };

  const calculateTotalDiscount = () => {
    return billData?.productDetails.reduce((sum, item) => {
      const mrp = parseFloat(item.productMRP) * item.quantity;
      const finalPrice = parseFloat(item.finalSellingPrice) * item.quantity;
      return sum + (mrp - finalPrice);
    }, 0);
  };

  const handleDownloadPDF = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Medical_Bill_${billData?.order_number}`,
    print: async (printIframe) => {
      // Create a new window and print to PDF
      const document =
        printIframe.contentDocument || printIframe.contentWindow?.document;
      if (document) {
        printIframe.contentWindow?.print();
      }
    },
    pageStyle: `
      @page {
        size: A4;
        margin: 0.5in;
      }
      @media print {
        body {
          font-family: Arial, sans-serif !important;
          font-size: 12px !important;
          line-height: 1.4 !important;
          color: #000 !important;
          background: white !important;
        }
        .no-print {
          display: none !important;
        }
        .print-only {
          display: block !important;
        }
        .break-inside-avoid {
          break-inside: avoid;
        }
        table {
          page-break-inside: auto;
        }
        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
      }
    `,
  });

  const totalMRP = calculateTotalMRP();
  const subtotal = calculateSubtotal();
  const totalDiscount = calculateTotalDiscount();

  const generateOrderBill = async () => {
    try {
      setIsLoading(true);
      // Uncomment and replace with your actual API call
      const response = await OrderService.generateBill(orderId,"ONLINE");
      if (response.success) {
        toast.success("Bill Generated Successfully");
        setBillData(response.data);
        // Navigate to bill page here
        // history.push(`/bill/${response.data.id}`)
      }
      console.log("\n\n response BILL", response, "\n\n");
      console.log("Generating bill for order:", orderId);
    } catch (error) {
      console.log("Bill Generation error", error, "\n\n");
      toast.error("Error While generating Bill");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    generateOrderBill();
  }, [orderId]);

  // Handle escape key press to close popup
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  if (!billData) return null;
  return (
    // Popup Overlay
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl h-[100dvh] overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <>
            {/* Popup Header */}
            <div className="p-4 border-b border-gray-200 bg-gray-50 no-print">
              <h2 className="text-xl font-semibold text-gray-800 text-center">
                Medical Bill - {billData?.order_number}
              </h2>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto h-[calc(100vh-145px)]">
              <div className="p-6">
                {/* Bill Content for Printing */}
                <div ref={componentRef}>
                  <div className="max-w-4xl mx-auto bg-white">
                    {/* Header */}
                    <div className="border-b-2 border-gray-200 pb-4 mb-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <h1 className="text-3xl font-bold text-gray-800 mb-2">
                            {billData?.clinicDetails.name}
                          </h1>
                          <p className="text-gray-600">
                            {billData?.clinicDetails.address}
                          </p>
                          <p className="text-gray-600">
                            {billData?.clinicDetails.city},{" "}
                            {billData?.clinicDetails.state} -{" "}
                            {billData?.clinicDetails.pincode}
                          </p>
                          <p className="text-gray-600">
                            Phone: {billData?.clinicDetails.mobileNumber}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="bg-blue-100 p-3 rounded-lg">
                            <FileText className="w-8 h-8 text-blue-600 mx-auto mb-1" />
                            <p className="text-sm font-semibold text-blue-800">
                              MEDICAL BILL
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bill Title and Order Info */}
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold text-gray-800 mb-2">
                        INVOICE
                      </h2>
                      <p className="text-lg font-semibold text-blue-600">
                        Order #{billData?.order_number}
                      </p>
                    </div>

                    {/* Patient and Order Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 break-inside-avoid">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">
                          Patient Details
                        </h3>
                        <div className="space-y-1 text-sm">
                          <p>
                            <span className="font-medium">Name:</span>{" "}
                            {billData?.patientDetails.name}
                          </p>
                          <p>
                            <span className="font-medium">Patient ID:</span>{" "}
                            {billData?.patientDetails.dhp_id}
                          </p>
                          <p>
                            <span className="font-medium">Mobile:</span>{" "}
                            {billData?.patientDetails.mobile_number}
                          </p>
                          <p>
                            <span className="font-medium">Email:</span>{" "}
                            {billData?.patientDetails.email}
                          </p>
                          <p>
                            <span className="font-medium">Gender:</span>{" "}
                            {billData?.patientDetails.gender}
                          </p>
                          <p>
                            <span className="font-medium">DOB:</span>{" "}
                            {formatDate(billData?.patientDetails.date_of_birth)}
                          </p>
                        </div>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">
                          Order Information
                        </h3>
                        <div className="space-y-1 text-sm">
                          <p>
                            <span className="font-medium">Order Date:</span>{" "}
                            {formatDate(billData?.order_date)}
                          </p>
                          <p>
                            <span className="font-medium">Payment Status:</span>
                            <span
                              className={`ml-2 px-2 py-1 rounded text-xs ${
                                billData?.payment_status === "COMPLETED"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {billData?.payment_status}
                            </span>
                          </p>
                          <p>
                            <span className="font-medium">Payment ID:</span>{" "}
                            {billData?.payment_id}
                          </p>
                          <p>
                            <span className="font-medium">
                              Delivery Address:
                            </span>{" "}
                            {billData?.order_address}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Clinic License Details */}
                    <div className="bg-blue-50 p-4 rounded-lg mb-6 break-inside-avoid">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        License Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <p>
                          <span className="font-medium">
                            Registration Number:
                          </span>{" "}
                          {billData?.clinicDetails.registrationNumber}
                        </p>
                        <p>
                          <span className="font-medium">
                            Drug License Number:
                          </span>{" "}
                          {billData?.clinicDetails.drugLicenseNumber}
                        </p>
                        <p>
                          <span className="font-medium">Supervisor:</span>{" "}
                          {billData?.clinicDetails.supervisorName}
                        </p>
                      </div>
                    </div>

                    {/* Product Details Table */}
                    <div className="mb-6 break-inside-avoid">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">
                        Items Ordered
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 p-3 text-left">
                                S.No.
                              </th>
                              <th className="border border-gray-300 p-3 text-left">
                                Item Name
                              </th>
                              <th className="border border-gray-300 p-3 text-left">
                                Description
                              </th>
                              <th className="border border-gray-300 p-3 text-right">
                                MRP
                              </th>
                              <th className="border border-gray-300 p-3 text-right">
                                Discount
                              </th>
                              <th className="border border-gray-300 p-3 text-right">
                                Unit Price
                              </th>
                              <th className="border border-gray-300 p-3 text-center">
                                Qty
                              </th>
                              <th className="border border-gray-300 p-3 text-right">
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {billData?.productDetails.map((item, index) => {
                              const itemTotal =
                                parseFloat(item.finalSellingPrice) *
                                item.quantity;
                              const discountAmount =
                                parseFloat(item.productMRP) -
                                parseFloat(item.finalSellingPrice);
                              const discountPercentage = (
                                (discountAmount / parseFloat(item.productMRP)) *
                                100
                              ).toFixed(1);

                              return (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="border border-gray-300 p-3">
                                    {index + 1}
                                  </td>
                                  <td className="border border-gray-300 p-3 font-medium">
                                    {item.productName}
                                  </td>
                                  <td className="border border-gray-300 p-3 text-sm text-gray-600">
                                    {item.description}
                                  </td>
                                  <td className="border border-gray-300 p-3 text-right">
                                    {formatCurrency(item.productMRP)}
                                  </td>
                                  <td className="border border-gray-300 p-3 text-right">
                                    <span className="text-green-600 font-medium">
                                      {formatCurrency(discountAmount)} (
                                      {discountPercentage}%)
                                    </span>
                                  </td>
                                  <td className="border border-gray-300 p-3 text-right">
                                    {formatCurrency(item.finalSellingPrice)}
                                  </td>
                                  <td className="border border-gray-300 p-3 text-center">
                                    {item.quantity}
                                  </td>
                                  <td className="border border-gray-300 p-3 text-right font-medium">
                                    {formatCurrency(itemTotal)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Bill Summary */}
                    <div className="flex justify-end mb-6 break-inside-avoid">
                      <div className="w-full md:w-96 bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">
                          Bill Summary
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Total MRP:</span>
                            <span>{formatCurrency(totalMRP)}</span>
                          </div>
                          <div className="flex justify-between text-green-600">
                            <span>Total Discount:</span>
                            <span>-{formatCurrency(totalDiscount)}</span>
                          </div>
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>GST (if applicable):</span>
                            <span>₹0.00</span>
                          </div>
                          <div className="border-t pt-2 mt-2">
                            <div className="flex justify-between text-lg font-bold text-gray-800">
                              <span>Grand Total:</span>
                              <span>
                                {formatCurrency(billData?.order_total)}
                              </span>
                            </div>
                          </div>
                          <div className="bg-green-50 p-2 rounded mt-2">
                            <div className="flex justify-between text-sm font-medium text-green-700">
                              <span>You Saved:</span>
                              <span>
                                {formatCurrency(totalDiscount)} (
                                {((totalDiscount / totalMRP) * 100).toFixed(1)}
                                %)
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t pt-4 text-center text-sm text-gray-600 break-inside-avoid">
                      <p className="mb-2">
                        Thank you for choosing {billData?.clinicDetails.name}
                      </p>
                      <p>
                        This is a computer-generated bill and does not require a
                        signature.
                      </p>
                      <p className="mt-2">
                        For any queries, please contact us at{" "}
                        {billData?.clinicDetails.mobileNumber}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center gap-4 p-4 border-t border-gray-200 bg-gray-50 no-print">
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
              >
                <Download className="w-5 h-5" />
                Download as PDF
              </button>
              <button
                onClick={onClose}
                className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
